import { Beacon } from "@knyt/artisan";
import { EventListenableObserver } from "./EventListenableObserver.js";
/**
 * A controller that manages an event listener on an element.
 *
 * @remarks
 *
 * This controller should be added before the host is connected.
 *
 * @example
 *
 * ```ts
 * import { createReference } from "@knyt/artisan";
 * import { EventListenerController } from "@knyt/tasker";
 *
 * new EventListenerController(host, {
 *   eventName: "click",
 *   listener(event) {
 *     console.info("click event", event);
 *   },
 *   target$: createReference(document.body),
 * });
 *
 * @public
 */
export class EventListenerController {
    #host;
    #observer;
    constructor(host, options) {
        this.#host = host;
        this.#observer = new EventListenableObserver(options);
        host.addController(this);
    }
    next(nextTarget) {
        this.#observer.next(nextTarget);
    }
    setup() {
        this.#observer.setup();
    }
    teardown() {
        this.#observer.teardown();
    }
    hostConnected() {
        this.#observer.setup();
    }
    hostDisconnected() {
        this.#observer.teardown();
    }
    /**
     * Removes the event listener from the target element
     * and disposes of the controller.
     */
    dispose() {
        // Prevent further setup or teardown
        this.#host.removeController(this);
        // Clean up the event listener
        this.#observer.dispose();
        if (!this.#disposeSignaler.hasTerminated) {
            // Emit a signal to indicate that the controller is disposed
            this.#disposeSignaler.next();
            // Complete the signaler to prevent further emissions
            this.#disposeSignaler.complete();
        }
    }
    #disposeSignaler = Beacon.withEmitter();
    get disposeSignal() {
        return this.#disposeSignaler.beacon;
    }
}
